import { Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export type ToastSeverity = 'success' | 'info' | 'warn' | 'error';

export interface ToastMessage {
  id?: number;
  severity: ToastSeverity | string;
  summary?: string;
  detail?: string;
  /** Stay on screen until dismissed. */
  sticky?: boolean;
  /** Milliseconds before auto-dismiss (default 4000). */
  life?: number;
}

/** App-wide toast queue, rendered by `app-toast-host` (replaces PrimeNG MessageService / p-toast). */
@Injectable({ providedIn: 'root' })
export class ToastMessageService {

  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private nextId = 1;

  readonly messages = signal<ToastMessage[]>([]);

  add(message: ToastMessage): void {
    // Do not stack identical toasts.
    const duplicate = this.messages().some((m) => m.severity === message.severity && m.summary === message.summary && m.detail === message.detail);

    if (duplicate) {
      return;
    }

    const toast = { ...message, id: this.nextId++ };
    this.messages.update((list) => [...list, toast]);

    if (this.isBrowser && !toast.sticky) {
      setTimeout(() => this.remove(toast.id!), toast.life ?? 4000);
    }
  }

  remove(id: number): void {
    this.messages.update((list) => list.filter((m) => m.id !== id));
  }

  clear(): void {
    this.messages.set([]);
  }
}
